// Tab Manager Utility - MME Analytics Platform
// Provides helper functions for managing tabs and components

export interface TabInfo {
  id: string;
  title: string;
  titleAr: string;
  component: string;
  category: string;
  status: 'active' | 'placeholder' | 'missing' | 'in-progress';
  adminOnly?: boolean;
}

// Complete tab registry with all unique IDs
export const TAB_REGISTRY: Record<string, TabInfo> = {
  // Overview Category
  'market-summary': {
    id: 'market-summary',
    title: 'Market Summary',
    titleAr: 'ملخص السوق',
    component: 'MarketSummary',
    category: 'overview',
    status: 'active'
  },
  'news-tape': {
    id: 'news-tape',
    title: 'News Tape',
    titleAr: 'شريط الأخبار',
    component: 'NewsTape',
    category: 'overview',
    status: 'active'
  },
  'data-table': {
    id: 'data-table',
    title: 'Data Table',
    titleAr: 'جدول البيانات',
    component: 'DataTable',
    category: 'overview',
    status: 'active'
  },
  'trading-day-status': {
    id: 'trading-day-status',
    title: 'Trading Status',
    titleAr: 'حالة التداول',
    component: 'TradingDayStatus',
    category: 'overview',
    status: 'active'
  },

  // Volume Analysis Category
  'volume-chart': {
    id: 'volume-chart',
    title: 'Volume Chart',
    titleAr: 'رسم الحجم',
    component: 'VolumeChart',
    category: 'volume-analysis',
    status: 'active'
  },
  'pure-volume-chart': {
    id: 'pure-volume-chart',
    title: 'Pure Volume (removed)',
    titleAr: 'رسم الحجم النقي (أزيل)',
    component: 'DetailedOptionsCharts',
    category: 'volume-analysis',
    status: 'missing'
  },
  'volume-delta-chart': {
    id: 'volume-delta-chart',
    title: 'Volume Delta (removed)',
    titleAr: 'حجم دلتا (أزيل)',
    component: 'DetailedOptionsCharts',
    category: 'volume-analysis',
    status: 'missing'
  },
  'trades-chart': {
    id: 'trades-chart',
    title: 'Trades Chart (removed)',
    titleAr: 'المعاملات (أزيل)',
    component: 'DetailedOptionsCharts',
    category: 'volume-analysis',
    status: 'missing'
  },
  'enhanced-volume': {
    id: 'enhanced-volume',
    title: 'Enhanced Volume',
    titleAr: 'حجم محسن',
    component: 'EnhancedVolumeChart',
    category: 'volume-analysis',
    status: 'active'
  },
  'strike-volume-chart': {
    id: 'strike-volume-chart',
    title: 'Strike Volume',
    titleAr: 'حجم سعر التنفيذ',
    component: 'StrikeVolumeChart',
    category: 'volume-analysis',
    status: 'active'
  },
  'custom-strike-volume': {
    id: 'custom-strike-volume',
    title: 'Custom Strike',
    titleAr: 'حجم مخصص',
    component: 'CustomStrikeVolumeChart',
    category: 'volume-analysis',
    status: 'active'
  },
  'pure-custom-strike-volume': {
    id: 'pure-custom-strike-volume',
    title: 'Pure Custom Strike',
    titleAr: 'حجم مخصص نقي',
    component: 'DetailedOptionsCharts',
    category: 'volume-analysis',
    status: 'missing'
  },

  // Options Analysis Category
  'iv-analysis': {
    id: 'iv-analysis',
    title: 'IV Analysis',
    titleAr: 'تحليل التقلب',
    component: 'IVAnalysis',
    category: 'options-analysis',
    status: 'active'
  },
  'edge-scanner': {
    id: 'edge-scanner',
    title: 'IV Scanner',
    titleAr: 'ماسح التقلب',
    component: 'EdgeScannerView',
    category: 'edge-journey',
    status: 'active'
  },
  'market-climate': {
    id: 'market-climate',
    title: 'Market Climate',
    titleAr: 'مناخ السوق',
    component: 'MarketClimateView',
    category: 'edge-journey',
    status: 'active'
  },
  'event-outlook': {
    id: 'event-outlook',
    title: 'Event Outlook',
    titleAr: 'آفاق الأحداث',
    component: 'EventOutlookView',
    category: 'news',
    status: 'active'
  },
  'path-cloud': {
    id: 'path-cloud',
    title: 'Path Cloud',
    titleAr: 'سحابة المسارات',
    component: 'PathCloudView',
    category: 'edge-journey',
    status: 'active'
  },
  'structure-lab': {
    id: 'structure-lab',
    title: 'Structure Lab',
    titleAr: 'مختبر الهيكل',
    component: 'StructureLabView',
    category: 'edge-journey',
    status: 'active'
  },
  'protect': {
    id: 'protect',
    title: 'Protect',
    titleAr: 'الحماية',
    component: 'ProtectView',
    category: 'protect',
    status: 'active'
  },
  'crypto': {
    id: 'crypto',
    title: 'Crypto',
    titleAr: 'الكريبتو',
    component: 'CryptoView',
    category: 'crypto',
    status: 'active'
  },
  'daily-edge': {
    id: 'daily-edge',
    title: 'Daily Edge List',
    titleAr: 'قائمة الأفضلية اليومية',
    component: 'DailyEdgeView',
    category: 'edge-journey',
    status: 'active'
  },
  'net-drift': {
    id: 'net-drift',
    title: 'Net Drift',
    titleAr: 'الانجراف الصافي',
    component: 'NetDriftView',
    category: 'gex',
    status: 'active'
  },
  'statement-tracker': {
    id: 'statement-tracker',
    title: 'Statement Tracker',
    titleAr: 'متتبع التصريحات',
    component: 'StatementTrackerView',
    category: 'news',
    status: 'active'
  },
  'news-feed': {
    id: 'news-feed',
    title: 'News Feed',
    titleAr: 'الأخبار',
    component: 'NewsFeedView',
    category: 'news',
    status: 'active'
  },
  'iv-tracking': {
    id: 'iv-tracking',
    title: 'IV Tracking',
    titleAr: 'تتبع التقلب الضمني',
    component: 'IvTrackingTable',
    category: 'options-analysis',
    status: 'active'
  },
  'strike-expiry-view': {
    id: 'strike-expiry-view',
    title: 'Strike Expiry',
    titleAr: 'سعر التنفيذ والتاريخ',
    component: 'StrikeExpiryView',
    category: 'options-analysis',
    status: 'missing'
  },
  'option-contract-helper': {
    id: 'option-contract-helper',
    title: 'Contract Helper',
    titleAr: 'مساعد العقد',
    component: 'OptionContractHelper',
    category: 'options-analysis',
    status: 'active'
  },
  'detailed-options-charts': {
    id: 'detailed-options-charts',
    title: 'Detailed Charts',
    titleAr: 'رسوم مفصلة',
    component: 'DetailedOptionsCharts',
    category: 'options-analysis',
    status: 'active'
  },
  'consolidated-analysis': {
    id: 'consolidated-analysis',
    title: 'Consolidated',
    titleAr: 'تحليل موحد',
    component: 'ConsolidatedAnalysis',
    category: 'options-analysis',
    status: 'active'
  },

  // Greeks Analysis Category
  'pure-delta-chart': {
    id: 'pure-delta-chart',
    title: 'Delta',
    titleAr: 'دلتا',
    component: 'PureDeltaChart',
    category: 'greeks-analysis',
    status: 'active'
  },
  'pure-gamma-chart': {
    id: 'pure-gamma-chart',
    title: 'Gamma',
    titleAr: 'جاما',
    component: 'PureGammaChart',
    category: 'greeks-analysis',
    status: 'active'
  },
  'pure-theta-chart': {
    id: 'pure-theta-chart',
    title: 'Theta',
    titleAr: 'ثيتا',
    component: 'PureThetaChart',
    category: 'greeks-analysis',
    status: 'active'
  },
  'pure-vega-chart': {
    id: 'pure-vega-chart',
    title: 'Vega',
    titleAr: 'فيجا',
    component: 'PureVegaChart',
    category: 'greeks-analysis',
    status: 'active'
  },
  'pure-gamma-shift': {
    id: 'pure-gamma-shift',
    title: 'Gamma Shift',
    titleAr: 'تحول جاما',
    component: 'PureGammaShiftChart',
    category: 'greeks-analysis',
    status: 'active'
  },
  'pure-spot-gamma': {
    id: 'pure-spot-gamma',
    title: 'Spot Gamma',
    titleAr: 'جاما الفوري',
    component: 'PureSpotGammaChart',
    category: 'greeks-analysis',
    status: 'active'
  },

  // Sentiment Analysis Category
  'sentiment': {
    id: 'sentiment',
    title: 'Sentiment',
    titleAr: 'تحليل المشاعر',
    component: 'SentimentAnalysis',
    category: 'sentiment-analysis',
    status: 'active'
  },
  'pure-sentiment-chart': {
    id: 'pure-sentiment-chart',
    title: 'Sentiment Chart',
    titleAr: 'رسم المشاعر',
    component: 'PureSentimentChart',
    category: 'sentiment-analysis',
    status: 'active'
  },
  'sentiment-alert-system': {
    id: 'sentiment-alert-system',
    title: 'Sentiment Alerts',
    titleAr: 'تنبيهات المشاعر',
    component: 'SentimentAlertSystem',
    category: 'sentiment-analysis',
    status: 'active'
  },
  'sentiment-widget': {
    id: 'sentiment-widget',
    title: 'Sentiment Widget',
    titleAr: 'أداة المشاعر',
    component: 'SentimentWidget',
    category: 'sentiment-analysis',
    status: 'active'
  },
  'enhanced-sentiment-chart': {
    id: 'enhanced-sentiment-chart',
    title: 'Enhanced Sentiment Chart',
    titleAr: 'رسم بياني المشاعر المحسن',
    component: 'EnhancedSentimentChart',
    category: 'sentiment-analysis',
    status: 'active'
  },

  // Technical Analysis Category
  'haltz-analysis': {
    id: 'haltz-analysis',
    title: 'Haltz Analysis',
    titleAr: 'تحليل هالتز',
    component: 'HaltzAnalysis',
    category: 'technical-analysis',
    status: 'missing'
  },
  'band-analysis': {
    id: 'band-analysis',
    title: 'Band Analysis',
    titleAr: 'تحليل الباند',
    component: 'BandAnalysis',
    category: 'technical-analysis',
    status: 'missing'
  },
  'zahn-analysis': {
    id: 'zahn-analysis',
    title: 'Zahn Analysis',
    titleAr: 'تحليل زان',
    component: 'ZahnAnalysis',
    category: 'technical-analysis',
    status: 'missing'
  },
  'bands-analysis': {
    id: 'bands-analysis',
    title: 'Bands Analysis',
    titleAr: 'تحليل الباندز',
    component: 'BandsAnalysis',
    category: 'technical-analysis',
    status: 'missing'
  },
  'combined-zahn-belt': {
    id: 'combined-zahn-belt',
    title: 'Zahn + Belt',
    titleAr: 'زان + باند',
    component: 'CombinedZahnBeltAnalysis',
    category: 'technical-analysis',
    status: 'missing'
  },
  'belt-ranges-analysis': {
    id: 'belt-ranges-analysis',
    title: 'Belt Ranges',
    titleAr: 'نطاقات الباند',
    component: 'BeltRangesAnalysis',
    category: 'technical-analysis',
    status: 'missing'
  },
  'flow-analysis': {
    id: 'flow-analysis',
    title: 'Flow Analysis',
    titleAr: 'تحليل التدفق',
    component: 'FlowAnalysis',
    category: 'technical-analysis',
    status: 'active'
  },

  // Advanced Charts Category
  'heatmap': {
    id: 'heatmap',
    title: 'Heatmap',
    titleAr: 'خريطة الحرارة',
    component: 'EnhancedHeatmap',
    category: 'advanced-charts',
    status: 'active'
  },
  'enhanced-heatmap': {
    id: 'enhanced-heatmap',
    title: 'Enhanced Heatmap',
    titleAr: 'خريطة محسنة',
    component: 'EnhancedHeatmap',
    category: 'advanced-charts',
    status: 'active'
  },
  'finviz-heatmap': {
    id: 'finviz-heatmap',
    title: 'FinViz Heatmap',
    titleAr: 'خريطة FinViz',
    component: 'FinVizHeatmap',
    category: 'advanced-charts',
    status: 'missing'
  },
  'square-heatmap': {
    id: 'square-heatmap',
    title: 'Square Heatmap',
    titleAr: 'خريطة مربعة',
    component: 'SquareHeatmap',
    category: 'advanced-charts',
    status: 'missing'
  },
  'pure-distribution-chart': {
    id: 'pure-distribution-chart',
    title: 'Distribution',
    titleAr: 'التوزيع',
    component: 'PureDistributionChart',
    category: 'advanced-charts',
    status: 'active'
  },
  'gamma-shift-chart': {
    id: 'gamma-shift-chart',
    title: 'Gamma Shift',
    titleAr: 'تحول جاما',
    component: 'GammaShiftChart',
    category: 'advanced-charts',
    status: 'placeholder'
  },
  'iv-squeezes': {
    id: 'iv-squeezes',
    title: 'IV Squeezes',
    titleAr: 'ضغط التقلب',
    component: 'IVSqueezes',
    category: 'advanced-charts',
    status: 'active'
  },

  // Alerts & Signals Category
  'alerts': {
    id: 'alerts',
    title: 'Alert System',
    titleAr: 'نظام التنبيهات',
    component: 'AlertSystem',
    category: 'alerts-signals',
    status: 'active'
  },
  'alert-preset-manager': {
    id: 'alert-preset-manager',
    title: 'Alert Presets',
    titleAr: 'إعدادات التنبيهات',
    component: 'AlertPresetManager',
    category: 'alerts-signals',
    status: 'active'
  },
  'trading-calls': {
    id: 'trading-calls',
    title: 'Trading Calls',
    titleAr: 'إشارات التداول',
    component: 'TradingCallSystem',
    category: 'alerts-signals',
    status: 'active'
  },
  'trading-call-tracker': {
    id: 'trading-call-tracker',
    title: 'Call Tracker',
    titleAr: 'متابع الإشارات',
    component: 'TradingCallTracker',
    category: 'alerts-signals',
    status: 'active'
  },
  'trading-calls-widget': {
    id: 'trading-calls-widget',
    title: 'Calls Widget',
    titleAr: 'أداة الإشارات',
    component: 'TradingCallsWidget',
    category: 'alerts-signals',
    status: 'active'
  },
  'alerts-widget': {
    id: 'alerts-widget',
    title: 'Alerts Widget',
    titleAr: 'أداة التنبيهات',
    component: 'AlertsWidget',
    category: 'alerts-signals',
    status: 'active'
  },

  // Mobile Tools Category
  'mobile-optimization': {
    id: 'mobile-optimization',
    title: 'Mobile Optimization',
    titleAr: 'تحسين الجوال',
    component: 'MobileOptimization',
    category: 'mobile-tools',
    status: 'placeholder'
  },
  'mobile-navigation': {
    id: 'mobile-navigation',
    title: 'Mobile Navigation',
    titleAr: 'تنقل الجوال',
    component: 'MobileNavigation',
    category: 'mobile-tools',
    status: 'placeholder'
  },
  'mobile-layout': {
    id: 'mobile-layout',
    title: 'Mobile Layout',
    titleAr: 'تخطيط الجوال',
    component: 'MobileLayout',
    category: 'mobile-tools',
    status: 'placeholder'
  },
  'mobile-chart-wrapper': {
    id: 'mobile-chart-wrapper',
    title: 'Chart Wrapper',
    titleAr: 'غلاف الرسوم',
    component: 'MobileChartWrapper',
    category: 'mobile-tools',
    status: 'placeholder'
  },
  'mobile-full-screen': {
    id: 'mobile-full-screen',
    title: 'Full Screen',
    titleAr: 'ملء الشاشة',
    component: 'MobileFullScreen',
    category: 'mobile-tools',
    status: 'placeholder'
  },
  'mobile-chart-view': {
    id: 'mobile-chart-view',
    title: 'Chart View',
    titleAr: 'عرض الرسوم',
    component: 'MobileChartView',
    category: 'mobile-tools',
    status: 'placeholder'
  },

  // Admin Tools Category
  'admin-dashboard': {
    id: 'admin-dashboard',
    title: 'Admin Dashboard',
    titleAr: 'لوحة الإدارة',
    component: 'AdminDashboard',
    category: 'admin-tools',
    status: 'active',
    adminOnly: true
  },
  'session-manager': {
    id: 'session-manager',
    title: 'Session Manager',
    titleAr: 'إدارة الجلسات',
    component: 'SessionManager',
    category: 'admin-tools',
    status: 'active',
    adminOnly: true
  },
  'symbol-mapping-status': {
    id: 'symbol-mapping-status',
    title: 'Symbol Mapping',
    titleAr: 'حالة الرموز',
    component: 'SymbolMappingStatus',
    category: 'admin-tools',
    status: 'active',
    adminOnly: true
  },
  'global-toggle-mapping': {
    id: 'global-toggle-mapping',
    title: 'Global Toggle',
    titleAr: 'التبديل العام',
    component: 'GlobalToggleMapping',
    category: 'admin-tools',
    status: 'active',
    adminOnly: true
  },
  'global-date-filter': {
    id: 'global-date-filter',
    title: 'Date Filter',
    titleAr: 'فلتر التاريخ',
    component: 'GlobalDateFilter',
    category: 'admin-tools',
    status: 'active',
    adminOnly: true
  },
  'global-alert-popup': {
    id: 'global-alert-popup',
    title: 'Global Alert',
    titleAr: 'تنبيه عام',
    component: 'GlobalAlertPopup',
    category: 'admin-tools',
    status: 'active',
    adminOnly: true
  },

  // Utilities Category
  'export-tools': {
    id: 'export-tools',
    title: 'Export Tools',
    titleAr: 'أدوات التصدير',
    component: 'ExportTools',
    category: 'utilities',
    status: 'active'
  },
  'file-upload': {
    id: 'file-upload',
    title: 'File Upload',
    titleAr: 'رفع الملفات',
    component: 'FileUpload',
    category: 'utilities',
    status: 'active'
  },
  'data-cleaning': {
    id: 'data-cleaning',
    title: 'Data Cleaning',
    titleAr: 'تنظيف البيانات',
    component: 'DataCleaning',
    category: 'utilities',
    status: 'active'
  },
  'advanced-analysis-controls': {
    id: 'advanced-analysis-controls',
    title: 'Analysis Controls',
    titleAr: 'تحكم التحليل',
    component: 'AdvancedAnalysisControls',
    category: 'utilities',
    status: 'active'
  },
  'feature-documentation': {
    id: 'feature-documentation',
    title: 'Feature Docs',
    titleAr: 'توثيق الميزات',
    component: 'FeatureDocumentation',
    category: 'utilities',
    status: 'active'
  }
};

// Helper functions for tab management
export const tabManager = {
  // Get tab info by ID
  getTabInfo: (id: string): TabInfo | null => {
    return TAB_REGISTRY[id] || null;
  },

  // Get all tabs in a category
  getTabsByCategory: (category: string): TabInfo[] => {
    return Object.values(TAB_REGISTRY).filter(tab => tab.category === category);
  },

  // Get all active tabs
  getActiveTabs: (): TabInfo[] => {
    return Object.values(TAB_REGISTRY).filter(tab => tab.status === 'active');
  },

  // Get all placeholder tabs
  getPlaceholderTabs: (): TabInfo[] => {
    return Object.values(TAB_REGISTRY).filter(tab => tab.status === 'placeholder');
  },

  // Get admin-only tabs
  getAdminTabs: (): TabInfo[] => {
    return Object.values(TAB_REGISTRY).filter(tab => tab.adminOnly);
  },

  // Check if tab exists
  tabExists: (id: string): boolean => {
    return id in TAB_REGISTRY;
  },

  // Get tab title by language
  getTabTitle: (id: string, language: 'en' | 'ar' = 'en'): string => {
    const tab = TAB_REGISTRY[id];
    if (!tab) return 'Unknown Tab';
    return language === 'ar' ? tab.titleAr : tab.title;
  },

  // Get component name for a tab
  getComponentName: (id: string): string | null => {
    const tab = TAB_REGISTRY[id];
    return tab ? tab.component : null;
  },

  // Get all tab IDs
  getAllTabIds: (): string[] => {
    return Object.keys(TAB_REGISTRY);
  },

  // Get tabs that need attention (placeholder or missing)
  getTabsNeedingAttention: (): TabInfo[] => {
    return Object.values(TAB_REGISTRY).filter(tab => 
      tab.status === 'placeholder' || tab.status === 'missing'
    );
  },

  // Search tabs by title
  searchTabs: (query: string): TabInfo[] => {
    const lowerQuery = query.toLowerCase();
    return Object.values(TAB_REGISTRY).filter(tab => 
      tab.title.toLowerCase().includes(lowerQuery) ||
      tab.titleAr.includes(query) ||
      tab.component.toLowerCase().includes(lowerQuery)
    );
  }
};

// Export the registry for direct access
export default TAB_REGISTRY; 
// Tabs that were retired in favour of newer ones. A saved or linked id that
// lands here is sent to its replacement instead of a blank view.
export const TAB_REDIRECTS: Record<string, string> = {
  'gex-chart': 'realtime-gex',
  'options-flow-legacy': 'options-flow',
  'flow-analyzer': 'analytics-hub',
  'iv-vol': 'iv-tracking',
  'market-analytics': 'market-climate',
  'flow-matrix-legacy': 'flow-matrix',
  // 2026-10-18: Top Premium Board (Smart Flow) retired into Options Tape presets.
  'smart-flow-scanner': 'options-flow',
};

export const DEFAULT_TAB_ID = 'data-table';

/**
 * Map a possibly stale tab id to a live one. `isKnown` tells whether an id
 * still exists in the current navigation; redirected targets are returned
 * as-is, unknown ids fall back to `fallback`.
 */
export function resolveTabId(
  id: string | null | undefined,
  isKnown: (id: string) => boolean,
  fallback: string = DEFAULT_TAB_ID,
): string {
  if (!id) return fallback;
  if (Object.prototype.hasOwnProperty.call(TAB_REDIRECTS, id)) return TAB_REDIRECTS[id];
  return isKnown(id) ? id : fallback;
}
